import { Button, cn } from "@scott/ui/client";
import type { Locale } from "date-fns";
import { startOfToday } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { useState } from "react";
import type { Matcher } from "react-day-picker";
import { Calendar } from "../calendar/calendar";
import { DatePlaceholder } from "../date-placeholder/date-placeholder";
import { FormattedDate } from "../formatted-date/formatted-date";
import { Popover, PopoverContent, PopoverTrigger } from "../popover/popover";

type DatePickerProps = {
  selectedDate: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  disabled?: boolean;
  className?: string;
  fromDate?: Date;
  toDate?: Date;
  locale: Partial<Locale>;
  country?: string;
  "data-testid"?: string;
};

export function DatePicker({
  selectedDate,
  onDateChange,
  disabled,
  className,
  fromDate,
  toDate,
  locale,
  country,
  "data-testid": testId,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  function buildDisabledMatcher(
    fromDate?: Date,
    toDate?: Date
  ): Matcher | Matcher[] | undefined {
    const matchers: Matcher[] = [];

    if (fromDate) {
      matchers.push({ before: fromDate });
    }
    if (toDate) {
      matchers.push({ after: toDate });
    }

    if (matchers.length === 0) {
      return;
    }
    if (matchers.length === 1) {
      return matchers[0];
    }
    return matchers;
  }

  return (
    <div
      className="relative inline-block w-full"
      data-testid={testId ? `${testId}-wrapper` : undefined}
    >
      <Popover modal onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <Button
            className={cn(
              `justify-between ${className}`,
              selectedDate ? "pr-8!" : ""
            )}
            disabled={disabled}
            variant="outline-no-hover"
          >
            {selectedDate ? (
              <FormattedDate
                country={country}
                dateOnly={getDateOnlyFromDate(selectedDate)}
              />
            ) : (
              <DatePlaceholder />
            )}
            <CalendarIcon
              className="ml-2 h-4 w-4"
              data-testid={testId ? `${testId}-calendar-icon` : undefined}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            defaultMonth={selectedDate ?? fromDate ?? startOfToday()}
            disabled={buildDisabledMatcher(fromDate, toDate)}
            endMonth={toDate}
            initialFocus
            locale={locale}
            mode="single"
            onSelect={(newDate) => {
              onDateChange(newDate);
              setOpen(false);
            }}
            selected={selectedDate}
            startMonth={fromDate}
          />
        </PopoverContent>
      </Popover>

      {selectedDate ? (
        <X
          className="-translate-y-1/2 absolute top-1/2 right-[12px] z-10 h-4 w-4 cursor-pointer text-muted-foreground hover:text-[#ef4444]"
          data-testid={testId ? `${testId}-clear-icon` : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onDateChange(undefined);
          }}
        />
      ) : null}
    </div>
  );
}

function getDateOnlyFromDate(date: Date): string {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
